import * as Yup from 'yup'
import type { IValidation, WorkspaceType } from './validation-types'

//all validation values across app can go here
const emailOrPhoneRegex: RegExp = /^(?:\+[1-10]{1}[0-9]{10,10}|\w+@\w+\.\w{2,3})$/

const emailRegex = /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/

export const alphaNumericCharacterRegex = /^[a-zA-Z0-9\s'-]*$/ // Regex to check if name does not have special characters or not except " ' ".

const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/

const DOLLAR_AMOUNT_REGEX = /^\d+(\.\d+)?$/

export const workspaceTypeValidationSchema = Yup.object({
  label: Yup.mixed<WorkspaceType>().oneOf(['Carrier', 'Broker', 'Forwarder', 'Warehouse', 'Shipper']),
  value: Yup.mixed<WorkspaceType>().oneOf(['Carrier', 'Broker', 'Forwarder', 'Warehouse', 'Shipper'])
}).test({
  test: (value) => {
    const invalid = !value.value && !value.label
    if (invalid) return new Yup.ValidationError('Workspace type is required', '', 'workspaceType')
    return true
  }
})

export const validations: IValidation = {
  appDatabase: Yup.string().required('Please enter a database name'),
  apiKey: Yup.string().required('Please enter an Api Key'),
  appPassword: Yup.string().required('Please enter a password'),
  appUsername: Yup.string().required('Please enter a username'),
  bio: Yup.string().required('Tell us something about you'),
  clientID: Yup.string().required('Please enter a client id key'),
  clientSecret: Yup.string().required('Please enter a client secret'),
  consumerKey: Yup.string().required('Please enter a consumer key'),
  consumerSecret: Yup.string().required('Please enter a consumer secret'),
  displayName: Yup.string().max(50, 'Must be 50 characters or less').required('Please enter a team or company name'),
  dotOrMCNumber: Yup.string().required('Please enter your DOT or MC number'),
  email: Yup.string().email('Invalid email address').required('Please enter your email'),
  emailOrPhone: Yup.string()
    .matches(emailOrPhoneRegex, 'Please enter a valid email address or phone number')
    .required('Please enter your email or phone'),
  emailPhone: Yup.string()
    .required('Email or phone number is required')
    .when('isPhoneNumber', {
      is: true,
      then: Yup.string().matches(phoneRegex, 'Please enter valid phone number'),
      otherwise: Yup.string().matches(emailRegex, 'Please enter a valid email address')
    }),
  name: Yup.string()
    .matches(alphaNumericCharacterRegex, 'Special characters are not allowed')
    .test('notEmpty', 'Please enter your name', (value) => value?.trim() !== '')
    .max(30, 'Must be 30 characters or less'),
  password: Yup.string().required('Password is required').min(8, 'Must be at least 8 characters'),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/, 'Phone number is not valid'),
  phoneNumber: Yup.string()
    .matches(/^(\+91-|\+91|0)?\d{10}$/, 'Phone number is not valid')
    .required('Required'),
  source: Yup.string().required('Please enter a source'),
  shipmentNumber: Yup.string().trim().required('Shipment number is required'),
  trackingLink: Yup.string().required().url('Please enter a valid URL').required('Please enter a tracking link'),
  workspacename: Yup.string().max(50, 'Must be 50 characters or less').required('Please enter a team or company name'),
  workspaceType: workspaceTypeValidationSchema,
  emailPrefix: Yup.string()
    .required('Please enter your email prefix')
    .trim()
    .matches(/^[^@\s]+$/, 'Please enter just the email prefix, not the entire email address.')
}

/**
 * Email or phone validation object that is used inside of keychaine `Form` component
 * */
export const emailPhoneValidation = Yup.object({
  emailPhone: Yup.string()
    .required('Email or phone number is required')
    .when('isPhoneNumber', {
      is: true,
      then: Yup.string().matches(phoneRegex, 'Please enter valid phone number'),
      otherwise: Yup.string().matches(emailRegex, 'Please enter a valid email address')
    })
})

export const workspaceValidation: Partial<IValidation> = {
  displayName: Yup.string().max(30, 'Must be 50 characters or less').required('Please enter a team or company name'),
  workspaceType: Yup.mixed<WorkspaceType>()
    .oneOf(['Carrier', 'Broker', 'Forwarder', 'Warehouse', 'Shipper'])
    .required('Workspace type is required')
}

const numberRegex = /^\d+(\.\d{1,2})?$/

/**
 * Validation schema for the qualification details form
 */
export const dotOrMcNumberFormValidationSchema = Yup.object().test({
  test: (value) => {
    if (!value.dotOrMCNumberValue)
      return new Yup.ValidationError('DOT# or MC# number is required', '', 'dotOrMCNumberValue')
    if (value.dotOrMcNumberValue && !numberRegex.test(value.dotOrMVNumberValue))
      return new Yup.ValidationError('The DOT# or MC# must be a valid number', '', 'dotOrMCNumberValue')

    return true
  }
})

/**
 * Validation schema for the offer amount input field
 */
export const validateOfferAmountInput = (value: string): string | null => {
  if (!DOLLAR_AMOUNT_REGEX.test(value) || !(parseFloat(value) > 0)) {
    return 'Please enter the dollar amount only.'
  }
  return null
}
